export type PaginationMeta = {
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  from: number;
  to: number;
  hasNext: boolean;
  hasPrev: boolean;
  mode: 'server' | 'client';
};

const toNumber = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasPaginationFields = (value: unknown): value is Record<string, any> =>
  isRecord(value) &&
  ['current_page', 'currentPage', 'last_page', 'lastPage', 'per_page', 'perPage', 'total'].some(
    (key) => typeof value[key] !== 'undefined',
  );

export function extractPaginatedData<T>(
  response: any,
  fallbackPage = 1,
  fallbackPerPage = 10,
): { items: T[]; meta: PaginationMeta } {
  let items: T[] = [];
  let metaSource: Record<string, any> | null = null;

  if (Array.isArray(response)) {
    items = response as T[];
  } else if (isRecord(response) && Array.isArray(response.data) && isRecord(response.pagination)) {
    items = response.data as T[];
    metaSource = response.pagination;
  } else if (isRecord(response) && Array.isArray(response.data) && hasPaginationFields(response)) {
    items = response.data as T[];
    metaSource = response;
  } else if (isRecord(response) && isRecord(response.data) && Array.isArray(response.data.data)) {
    items = response.data.data as T[];
    metaSource = hasPaginationFields(response.data)
      ? response.data
      : isRecord(response.pagination)
        ? response.pagination
        : null;
  } else if (isRecord(response) && Array.isArray(response.items)) {
    items = response.items as T[];
    metaSource = response;
  } else if (isRecord(response) && Array.isArray(response.data)) {
    items = response.data as T[];
  } else if (isRecord(response) && isRecord(response.data) && Array.isArray(response.data.items)) {
    items = response.data.items as T[];
    metaSource = hasPaginationFields(response.data) ? response.data : null;
  }

  const hasServerMeta =
    !!metaSource &&
    ['current_page', 'currentPage', 'last_page', 'lastPage', 'total'].some(
      (key) => typeof metaSource?.[key] !== 'undefined',
    );

  const total = hasServerMeta ? toNumber(metaSource?.total, items.length) : items.length;
  const perPage = hasServerMeta
    ? toNumber(metaSource?.per_page ?? metaSource?.perPage, fallbackPerPage)
    : Math.max(items.length, fallbackPerPage);
  const lastPage = hasServerMeta
    ? Math.max(toNumber(metaSource?.last_page ?? metaSource?.lastPage, 1), 1)
    : Math.max(Math.ceil(total / Math.max(perPage, 1)), 1);
  const currentPage = hasServerMeta
    ? Math.min(Math.max(toNumber(metaSource?.current_page ?? metaSource?.currentPage, fallbackPage), 1), lastPage)
    : Math.min(Math.max(fallbackPage, 1), lastPage);

  const defaultFrom = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const from = hasServerMeta
    ? toNumber(metaSource?.from, defaultFrom)
    : defaultFrom;
  const defaultTo = total === 0 ? 0 : Math.min(from + items.length - 1, total);
  const to = hasServerMeta
    ? toNumber(metaSource?.to, defaultTo)
    : defaultTo;

  return {
    items,
    meta: {
      currentPage,
      lastPage,
      total,
      perPage,
      from,
      to,
      hasNext: currentPage < lastPage,
      hasPrev: currentPage > 1,
      mode: hasServerMeta ? 'server' : 'client',
    },
  };
}

export function paginateClientData<T>(
  allItems: T[],
  page: number,
  perPage = 10,
): { items: T[]; meta: PaginationMeta } {
  const total = allItems.length;
  const lastPage = Math.max(Math.ceil(total / Math.max(perPage, 1)), 1);
  const currentPage = Math.min(Math.max(page, 1), lastPage);
  const startIndex = (currentPage - 1) * perPage;
  const items = allItems.slice(startIndex, startIndex + perPage);
  const from = total === 0 ? 0 : startIndex + 1;
  const to = total === 0 ? 0 : Math.min(startIndex + items.length, total);

  return {
    items,
    meta: {
      currentPage,
      lastPage,
      total,
      perPage,
      from,
      to,
      hasNext: currentPage < lastPage,
      hasPrev: currentPage > 1,
      mode: 'client',
    },
  };
}
